import React, {FunctionComponent, useEffect, useState} from 'react';
import {Option, route, useForm} from 'b302-frontend-library';

import {useNavigate} from 'react-router-dom';

import University from '../../../../networking/models/University';
import HanInput from '../../../utils/HanInput';
import HanTextArea from '../../../utils/HanTextArea';
import getIconForSocialMediaSlug, {MediaAccount} from '../../../../utils/icon';
import HanButton from '../../../utils/HanButton';
import HanDropdown from '../../../utils/HanDropdown';
import ImageUpload from '../../../partials/ImageUpload';
import {useCountries} from '../../../../contexts/CountryContext';
import {getUniversityUrlForSlug} from '../../../../utils/mediaAccount';
import {UniversityFormRequest} from '../../../../networking/repos/UniversityRepository';
import {CountriesForm} from "../../pages/Countries/CountriesContent";
import {FV} from '../../../../utils/Validation';

interface Image {
    number: number;
    file: File;
}

export interface UniversitySaveEvent {
    data: UniversityFormRequest;
    images: Image[];
    imagesToDelete: number[];
}

interface Props {
    university?: University;
    onUniversitySave: (e: UniversitySaveEvent) => void;
}

const UniversityForm: FunctionComponent<Props> = ({university, onUniversitySave}) => {
    const navigate = useNavigate();
    const {countries} = useCountries();

    const maxImages = 5;

    const initialState: UniversityFormRequest = { // TODO: Set initial state from university dto
        name: university?.name,
        description: university?.description,
        latitude: university?.latitude?.toString(),
        longitude: university?.longitude?.toString(),
        country: university?.country?.id,
        city: university?.city,
        rating: university?.rating?.toString(),
        facebook: university && getUniversityUrlForSlug(university, MediaAccount.FACEBOOK),
        twitter: university && getUniversityUrlForSlug(university, MediaAccount.TWITTER),
        instagram: university && getUniversityUrlForSlug(university, MediaAccount.INSTA),
        website: university && getUniversityUrlForSlug(university, MediaAccount.WEB)
    };

    const [form, onUniversityFormChange] = useForm<Required<UniversityFormRequest>>(f => ({
        name: f.input(initialState.name ?? '')
            .size(1, 100),

        description: f.input(initialState.description ?? ''),
        latitude: f.input(initialState.latitude ?? '')
            .cValidations([FV.range(-90, 90)]),

        longitude: f.input(initialState.longitude ?? '')
            .cValidations([FV.range(-180, 180)]),

        country: f.select((initialState.country ?? -1), countries.map(c => c.id) as number[]),
        city: f.input(initialState.city ?? '')
            .size(1, 255),
        rating: f.input(initialState.rating ?? ''),
        facebook: f.input(initialState.facebook ?? ''),
        twitter: f.input(initialState.twitter ?? ''),
        instagram: f.input(initialState.instagram ?? ''),
        website: f.input(initialState.website ?? ''),
    }));

    const [images, setImages] = useState<Image[]>([]);
    const [imagesToDelete, setImagesToDelete] = useState<number[]>([]);
    const [saved, setSaved] = useState<boolean>(true);

    useEffect(() => {
        setSaved(!dataHasChanged(form.values));
    }, [form.values]);

    const dataHasChanged = (data: UniversityFormRequest) => {
        let changed = false;

        const keys = Object.keys(initialState) as (keyof UniversityFormRequest)[];

        for (const key of keys) {
            if (data[key] !== initialState[key]) {
                changed = true;
                break;
            }
        }

        return changed;
    };

    const getIcon = (type: MediaAccount) => {
        const Icon = getIconForSocialMediaSlug(type);

        return <Icon className={'university-form__icon'}/>;
    };

    const onImageUpload = (number: number, file: File) => {
        const newImages = [...images];
        const index = images.findIndex(i => i.number === number);

        if (index === -1) {
            newImages.push({number, file});
        } else {
            newImages[index].file = file;
        }

        const newImagesToDelete = [...imagesToDelete];
        newImagesToDelete.splice(newImagesToDelete.findIndex(i => i === number - 1), 1);

        setImages(newImages);
        setImagesToDelete(newImagesToDelete);
        setSaved(false);
    };

    const onImageDelete = (number: number) => {
        const index = images.findIndex(i => i.number === number);

        const newImages = [...images];
        newImages.splice(index, 1);

        setImages(newImages);
        setImagesToDelete([...imagesToDelete, number + 1]);
        setSaved(false);
    };

    const openResponses = () => {
        navigate(route('admin-responses', {id: university?.id}));
    };

    const saveUniversity = () => {
        setSaved(true);

        onUniversitySave({
            data: form.values,
            images,
            imagesToDelete
        });
    };

    const getInitialImageForIndex = (index: number) => {
        return university?.images?.find(i => i.order === index + 1)?.url ?? '';
    };

    return (
        <div className={'university-form'}>
            {/*<Prompt message={'Discard changes?'} when={!saved} />*/}

            <div className={'university-form__header'}>
                <h1>FORM</h1>

                <HanButton onClick={saveUniversity} disabled={!form.isValid || saved}>Save</HanButton>
            </div>

            <div className={'university-form__body'}>
                <div className={'university-form__content--left'}>
                    <div className={'university-form__form-group'}>
                        <div className={'flex flex--align-center flex--justify-between'}>
                            <p className={'university-form__field-title'}>Name University</p>
                        </div>

                        <HanInput id={'name'} form={form} onChange={onUniversityFormChange}/>
                    </div>

                    <div className={'university-form__form-group'}>
                        <p className={'university-form__field-title'}>Intro text</p>

                        <HanTextArea
                            id={'description'}
                            form={form}
                            onChange={onUniversityFormChange}
                            className={'university-form__textarea'}/>
                    </div>

                    <div className={'university-form__form-group'}>
                        <p className={'university-form__field-title'}>Social media links</p>

                        <div className={'university-form__input-group'}>
                            <HanInput id={'instagram'} form={form}
                                      onChange={onUniversityFormChange}>{getIcon(MediaAccount.INSTA)}</HanInput>
                            <HanInput id={'twitter'} form={form}
                                      onChange={onUniversityFormChange}>{getIcon(MediaAccount.TWITTER)}</HanInput>
                            <HanInput id={'facebook'} form={form}
                                      onChange={onUniversityFormChange}>{getIcon(MediaAccount.FACEBOOK)}</HanInput>
                            <HanInput id={'website'} form={form}
                                      onChange={onUniversityFormChange}>{getIcon(MediaAccount.WEB)}</HanInput>
                        </div>
                    </div>

                    <div className={'university-form__form-group'}>
                        <p className={'university-form__field-title university-form__field-title--extra-space'}>Responses
                            ({university?.responses})</p>

                        <HanButton onClick={openResponses}>Go to responses</HanButton>
                    </div>
                </div>

                <div className={'university-form__content--right'}>
                    <div className={'university-form__form-group university-form__images'}>
                        <p className={'university-form__field-title'}>
                            Images
                            <span className={'university-form__field-title--grey'}>Upload an image with a minimum resolution of 800x600px</span>
                        </p>

                        <div className={'university-form__images-list'}>
                            {(Array(maxImages).fill(1)).map((v, index) => (
                                <ImageUpload
                                    onUpload={(file) => onImageUpload(index, file)}
                                    onDelete={() => onImageDelete(index)}
                                    image={getInitialImageForIndex(index)}
                                    key={index}/>
                            ))}
                        </div>
                    </div>

                    <div className={'university-form__last-section'}>
                        <div className={'flex flex--column flex--justify-between'}>
                            <div className={'university-form__form-group'}>
                                <p className={'university-form__field-title'}>Country</p>
                                <HanDropdown placeholder={'Country'}
                                             form={form}
                                             onChange={onUniversityFormChange}
                                             id={'country'}>
                                    { (item, i, onClick) => {
                                        const country = countries.find(c => c.id === item)!;

                                        return <Option key={country.id} onClick={() => onClick(i, item)}>{country.name}</Option>
                                    } }
                                </HanDropdown>
                            </div>

                            <div className={'university-form__form-group'}>
                                <p className={'university-form__field-title'}>CITY, CAMPUS</p>

                                <HanInput id={'city'} form={form} onChange={onUniversityFormChange}/>
                            </div>

                            <div className={'university-form__form-group'}>
                                <p className={'university-form__field-title'}>Costs</p>

                                <p>Average total costs: € {university?.totalCost}</p>
                            </div>
                        </div>

                        <div>
                            <div className={'university-form__form-group'}>
                                <p className={'university-form__field-title university-form__field-title--extra-space'}>Maps
                                    Coordinates</p>

                                <div className={'university-form__input-group'}>
                                    <HanInput id={'latitude'} form={form} placeholder={'Latitude'}
                                              showErrors
                                              onChange={onUniversityFormChange}/>
                                    <HanInput id={'longitude'} form={form} placeholder={'Longitude'}
                                              onChange={onUniversityFormChange}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UniversityForm;
